import { formatTicker } from '@api';
import { usePriceChangeMarketReactionData } from '@terminal/hooks/use-price-change-market-reaction-data/usePriceChangeMarketReactionData';
import { usePriceMarketReactionData } from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { LineData } from '@terminal/modules/market-reaction-charts/types';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';

import { MarketCatalystReactionChartData } from '../market-catalyst-reaction.types';

type UseMultipleEtfMarketReactionLinesArgs = {
  tags: string[];
  tickers: string[];
  createdAt: string;
};

export const useMultipleEtfMarketReactionLines = ({
  tags,
  tickers,
  createdAt,
}: UseMultipleEtfMarketReactionLinesArgs): MarketCatalystReactionChartData => {
  const {
    fromDateEST,
    toDateEST,
    isLoading: isPriceMarketReactionDataLoading,
  } = usePriceMarketReactionData({
    entityTag: tags[0],
    ticker: tickers[0],
    reportingDate: createdAt,
    isMultipleEntitiesChart: true,
    variant: 'catalyst',
    isMarketCatalyst: true,
  });

  const { data: entitiesData, isLoading: isEntitiesLoading } = useEntities({
    entityTags: tags,
  });

  const {
    data: priceChangeData,
    isLoading: isPriceChangeMarketReactionDataLoading,
  } = usePriceChangeMarketReactionData({
    tags,
    tickers,
    fromDateEST,
    toDateEST,
    enabled: true,
  });

  // priceChangeData and entitiesData are both ordered by `tags`, so we can pair
  // them by index. Only build lines once every entity has resolved to avoid
  // emitting a line without a subject.
  const lines: LineData[] = priceChangeData.every(
    (_, index) => entitiesData[index]
  )
    ? priceChangeData.map((line, index) => ({
        ...line,
        label: formatTicker(entitiesData[index]),
        subject: { type: 'entity', data: entitiesData[index] },
      }))
    : [];

  return {
    lines,
    fromDateEST,
    toDateEST,
    isLoading:
      isPriceMarketReactionDataLoading ||
      isPriceChangeMarketReactionDataLoading ||
      isEntitiesLoading,
  };
};
